import { Box, Flex, HStack, Stack, Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import * as m from 'motion/react-m';

import { PhoneFrame } from './PhoneFrame';

const CYCLE_MS = 8000;

/** Mic / hang-up, drawn in the same 24x24 stroke style as the rest of the landing icons. */
const MicGlyph = () => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="9" y="2" width="6" height="11" rx="3" />
    <path d="M5 10v1a7 7 0 0 0 14 0v-1" />
    <line x1="12" y1="19" x2="12" y2="22" />
  </svg>
);

const LeaveGlyph = () => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 10a14 14 0 0 1 18 0v3.5a1.5 1.5 0 0 1-1.7 1.5l-2.5-.4a1.5 1.5 0 0 1-1.3-1.5v-1.4a11 11 0 0 0-7 0v1.4a1.5 1.5 0 0 1-1.3 1.5l-2.5.4A1.5 1.5 0 0 1 3 13.5z" />
  </svg>
);

type Msg = { side: 'in' | 'out'; text: string; delay: number };

const MESSAGES: Msg[] = [
  { side: 'in', text: 'yo, ranked tonight?', delay: 0.4 },
  { side: 'out', text: "for sure - 9pm?", delay: 1.3 },
  { side: 'in', text: "perfect, I'll grab a third", delay: 2.4 },
  { side: 'in', text: 'found one - hop in voice 🎧', delay: 5.0 },
];

const Bubble = ({ msg }: { msg: Msg }) => {
  const isOut = msg.side === 'out';
  return (
    <Flex justifyContent={isOut ? 'flex-end' : 'flex-start'} w="100%" asChild>
      <m.div
        initial={{ opacity: 0, y: 8, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ delay: msg.delay, duration: 0.35, ease: 'easeOut' }}
      >
        <Box
          maxW="78%"
          px={3.5}
          py={2}
          borderRadius="18px"
          bg={isOut ? 'purple.500' : 'whiteAlpha.100'}
          color={isOut ? 'white' : 'whiteAlpha.900'}
          fontSize="12px"
          lineHeight="1.4"
        >
          {msg.text}
        </Box>
      </m.div>
    </Flex>
  );
};

const TypingDots = () => (
  <Flex justifyContent="flex-start" w="100%" asChild>
    <m.div
      initial={{ opacity: 0 }}
      animate={{ opacity: [0, 1, 1, 0] }}
      transition={{ delay: 3.4, duration: 1.5, times: [0, 0.15, 0.75, 1] }}
    >
      <Flex bg="whiteAlpha.100" borderRadius="18px" px={3.5} py={2.5} gap={1} alignItems="center">
        {[0, 1, 2].map((i) => (
          <Box key={i} w="6px" h="6px" borderRadius="full" bg="whiteAlpha.500" asChild>
            <m.div
              animate={{ opacity: [0.3, 1, 0.3], y: [0, -2, 0] }}
              transition={{ duration: 0.9, repeat: Infinity, delay: 3.4 + i * 0.18 }}
            />
          </Box>
        ))}
      </Flex>
    </m.div>
  </Flex>
);

const Screen = () => (
  <Flex direction="column" h="100%" pt="34px">
    {/* header */}
    <Flex alignItems="center" gap={2.5} px={3} pb={2.5} borderBottom="1px solid" borderColor="whiteAlpha.100">
      <Text color="fg.muted" fontSize="18px">
        ‹
      </Text>
      <Flex
        w="30px"
        h="30px"
        borderRadius="full"
        alignItems="center"
        justifyContent="center"
        fontSize="12px"
        fontWeight="800"
        color="white"
        position="relative"
        style={{ background: 'linear-gradient(135deg, #4B50E6 0%, #8E7BFF 130%)' }}
      >
        A
        <Box position="absolute" bottom="0" right="0" w="8px" h="8px" borderRadius="full" bg="#22c55e" border="2px solid" borderColor="hsl(228,11.6%,7%)" />
      </Flex>
      <Box>
        <Text fontSize="13px" fontWeight="700" color="fg.emphasized" lineHeight="1.1">
          Alex
        </Text>
        <Text fontSize="10px" color="#22c55e" lineHeight="1.1">
          online
        </Text>
      </Box>
    </Flex>

    {/* Nearest session of the peer — mirrors PeerSessionBar: game - time - seats - action. */}
    <Flex
      alignItems="center"
      gap={2}
      px={3}
      h="34px"
      borderBottom="1px solid"
      borderColor="whiteAlpha.100"
      asChild
    >
      <m.div
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15, duration: 0.3, ease: 'easeOut' }}
      >
        <Flex
          alignItems="center"
          gap="4px"
          px="6px"
          h="16px"
          borderRadius="full"
          bg="rgba(34,197,94,0.14)"
          color="#4ade80"
          fontSize="8px"
          fontWeight="800"
          letterSpacing="0.06em"
          flexShrink={0}
        >
          <Box w="4px" h="4px" borderRadius="full" bg="#22c55e" asChild>
            <m.div
              animate={{ opacity: [1, 0.35, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            />
          </Box>
          LIVE
        </Flex>
        <Text fontSize="10px" color="fg.muted" whiteSpace="nowrap" flex={1} lineHeight="1.1">
          Ranked · 21:00
        </Text>
        <Text fontSize="10px" fontWeight="700" color="#4ade80" flexShrink={0}>
          2/5
        </Text>
        <Flex
          alignItems="center"
          px={2}
          h="20px"
          borderRadius="md"
          bg="whiteAlpha.200"
          color="white"
          fontSize="9px"
          fontWeight="700"
          flexShrink={0}
        >
          Join team
        </Flex>
      </m.div>
    </Flex>

    {/* messages */}
    <Stack gap={2} px={3} py={3} flex={1} justifyContent="flex-end">
      {MESSAGES.map((msg) => (
        <Bubble key={msg.text} msg={msg} />
      ))}
      <TypingDots />
    </Stack>

    {/* Voice room on the site - mirrors VoiceRoomBlock in the "joined" state:
        green accent, who is in, live wave, mic + leave. No Discord: voice is ours. */}
    <Box px={3} pb={3} asChild>
      <m.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 5.8, duration: 0.4, ease: 'easeOut' }}
      >
        <Flex
          alignItems="center"
          gap={2}
          p={2}
          borderRadius="xl"
          bg="whiteAlpha.100"
        >
          {/* who is in voice */}
          <Flex flexShrink={0}>
            {['A', 'M'].map((letter, i) => (
              <Flex
                key={letter}
                w="20px"
                h="20px"
                borderRadius="full"
                alignItems="center"
                justifyContent="center"
                fontSize="9px"
                fontWeight="800"
                color="white"
                ml={i === 0 ? 0 : '-6px'}
                border="2px solid"
                borderColor="hsl(228,11.6%,7%)"
                style={{
                  background:
                    i === 0
                      ? 'linear-gradient(135deg, #4B50E6 0%, #8E7BFF 130%)'
                      : 'linear-gradient(135deg, #22c55e 0%, #4ade80 130%)',
                }}
              >
                {letter}
              </Flex>
            ))}
          </Flex>

          <Box flex={1} minW={0}>
            <Text fontSize="11px" fontWeight="700" color="#4ade80" lineHeight="1.2">
              You&rsquo;re in voice
            </Text>
            <Text fontSize="9px" color="fg.muted" lineHeight="1.2">
              Alex is speaking
            </Text>
          </Box>

          <HStack gap="2px" alignItems="flex-end" h="12px" flexShrink={0}>
            {[0, 1, 2, 3].map((i) => (
              <Box key={i} w="2px" borderRadius="full" bg="#4ade80" asChild>
                <m.div
                  animate={{ height: ['4px', '11px', '4px'] }}
                  transition={{ duration: 0.7, repeat: Infinity, delay: i * 0.12 }}
                />
              </Box>
            ))}
          </HStack>

          {/* mic + leave, the two controls the real block keeps unlabeled */}
          <Flex gap="4px" flexShrink={0}>
            <Flex
              w="22px"
              h="22px"
              borderRadius="md"
              bg="whiteAlpha.200"
              color="white"
              alignItems="center"
              justifyContent="center"
              fontSize="10px"
            >
              <MicGlyph />
            </Flex>
            <Flex
              w="22px"
              h="22px"
              borderRadius="md"
              bg="rgba(239,68,68,0.18)"
              color="#ef4444"
              alignItems="center"
              justifyContent="center"
            >
              <LeaveGlyph />
            </Flex>
          </Flex>
        </Flex>
      </m.div>
    </Box>
  </Flex>
);

export const ChatMock = () => {
  const [cycle, setCycle] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setCycle((c) => c + 1), CYCLE_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <PhoneFrame>
      <Screen key={cycle} />
    </PhoneFrame>
  );
};
